/**
 * The decisions the intro card turns on, kept out of the components so they are
 * unit-tested without a rendering toolchain — the house style here.
 *
 * Shared by three callers, which is the point: the live card at /prompt, the
 * docked nudge, and the console preview. A preview computing "what will the card
 * say" differently from the product would be worse than no preview at all.
 *
 * Mirrors the API's `UserFormPromptPackExtensions` (CardTitleOf, CardLineOf,
 * ModeOf) and the app's `user_form_prompt_card.dart` — change all three together.
 *
 * House style: imported explicitly, never auto-imported.
 */

/** A week, matching `UserFormPromptSnoozeRules.SnoozeDays` on the server. */
export const PROMPT_SNOOZE_DAYS = 7;

/** Declines before a set is taken as a no. The server is what enforces it. */
export const PROMPT_MAX_STRIKES = 3;

/** Routes a nudge would be rude on: someone is mid-task or mid-decision. */
export const PROMPT_QUIET_ROUTES = Object.freeze([
  '/prompt', '/details-required', '/legal', '/contact', '/meets/offer/', '/pay/', '/embed', '/email-preferences',
  // The anonymous outreach feedback page: one short form, opened from an email.
  '/feedback/',
  // Mid-wizard: on a phone the nudge sat over the club name field.
  '/clubs/create', '/invite/', '/validate/', '/register', '/login',
  // The user tag step is a wall of its own (PRIVACY_PLAN.md §15): one ask at a time.
  '/welcome/',
]);

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * What the card's heading will actually say.
 *
 * Blank is "no override", not an empty card — so clearing the box in the console
 * gets the derived words back rather than a heading of nothing. Falls through to
 * the first form's title, which is what every prompt that predated sets got on
 * migration without an admin editing it.
 */
export function promptCardTitle(cardTitle, firstPrompt) {
  return trimmed(cardTitle) || trimmed(firstPrompt?.title) || 'One quick thing';
}

/** The supporting line, with the same fallback. Null when there is none. */
export function promptCardLine(cardLine, firstPrompt) {
  return trimmed(cardLine) || trimmed(firstPrompt?.intro) || null;
}

/**
 * The strictest mode any form in the set carries.
 *
 * One required form makes the whole card required: letting somebody decline the
 * card would walk them straight past the required form behind it. An EMPTY set
 * is optional — there is nothing to be required about.
 */
export function promptCardMode(prompts) {
  return (prompts ?? []).some(p => p?.mode === 1 || p?.mode === 'required')
    ? 'required'
    : 'optional';
}

/**
 * The sentence the card shows when the run asks the member to accept the terms.
 * The live card takes it from the SERVER (`card.termsLine`), which is where the
 * words live; this copy is only for the console preview, which has no member to
 * ask. Mirror of the API's `UserFormPromptCardResponse.TermsToAcceptLine`.
 */
export const PROMPT_TERMS_LINE =
  'You need to read and accept our Terms of Use and Privacy Policy to keep using Tap Leagues.';

const TERMS_BINDINGS = ['consent.termsOfUse', 'consent.privacyPolicy'];

/**
 * The forms the card lists, so it describes the whole run it opens rather than
 * only its first page: a card about the terms that then asks for a county two
 * pages later is a surprise (UAT, 27 Sep 2026). From the server's `card.steps`;
 * empty from a server that predates them, when the card shows no list.
 */
export function promptCardSteps(card) {
  return (card?.steps ?? [])
    .filter(s => trimmed(s?.title))
    .map(s => ({title: trimmed(s.title), optional: s.optional === true, terms: s.terms === true}));
}

/** The card as the components draw it, from an unsaved console draft. */
export function promptCardFromDraft(draft) {
  const prompts = draft?.prompts ?? [];
  const first = prompts[0];
  const steps = prompts.map(p => ({
    title: trimmed(p?.title),
    optional: !(p?.mode === 1 || p?.mode === 'required'),
    terms: (p?.fields ?? []).some(f => TERMS_BINDINGS.includes(f?.binding)),
  })).filter(s => s.title);
  return {
    title: promptCardTitle(draft?.cardTitle, first),
    line: promptCardLine(draft?.cardLine, first),
    mode: promptCardMode(prompts),
    steps,
    termsLine: steps.some(s => s.terms) ? PROMPT_TERMS_LINE : null,
  };
}

/**
 * Whether this run is introduced by a card at all.
 *
 * The card's whole job is warning an EXISTING member that they are about to be
 * asked for something further. Somebody who finished signing up ninety seconds
 * ago needs no warning: these ARE the sign-up questions, and a card would be one
 * more screen between them and the one they were promised.
 *
 * `signingUp` is decided on the SERVER (the account was created after the set
 * went live) and mirrored here and in the app's `user_form_prompt_card.dart`
 * only so the three describe the same rule. See the API's
 * `UserFormPromptPackExtensions.IsSigningUp`.
 *
 * It changes the card and NOTHING else. The set is still required, the wall is
 * still the route, and the form still has one way out. A client reading this as
 * "they are mid sign-up, leave them alone" would walk every new member straight
 * past the terms.
 */
export function promptCardIntroduces({card, signingUp, cardSeen} = {}) {
  if (!card || cardSeen || signingUp) return false;
  return true;
}

/**
 * Whether /prompt was opened from the docked nudge, whose "Sure" already
 * answered the card. The router here hands query values back parsed, so
 * `started=1` arrives as the NUMBER 1 and a strict compare with '1' never
 * matched: the member pressed Sure and got the same card again (27 Sep 2026).
 */
export const cameFromPromptNudge = (query) => String(query?.started ?? '') === '1';

/** A required card has one button: no close, no "later", no tap-outside. */
export const promptCardIsRequired = (card) => card?.mode === 'required';

/** When to ask again after a "Not now". */
export function promptSnoozeUntil(now = new Date()) {
  return new Date(now.getTime() + PROMPT_SNOOZE_DAYS * 86400000).toISOString();
}

/**
 * Whether the docked nudge belongs on screen.
 *
 * A REQUIRED set is never drawn here, deliberately: its blocking is the route
 * redirect in `userFormPrompt.global.js`, and moving that into a mounted
 * component would make the guarantee depend on the component mounting.
 */
export function shouldShowPromptDock({authenticated, pending, path, hidden}) {
  if (!authenticated || hidden) return false;
  if (!pending?.id || !pending?.card) return false;
  // A run the SERVER calls required is a wall however its first form is marked.
  // The tester on UAT met the terms as a docked card with "Sure" because the
  // first form of a required run arrived as an offer (27 Sep 2026).
  if (pending.mode === 'required' || pending.card.mode === 'required') return false;
  return !PROMPT_QUIET_ROUTES.some(p => path === p || (path ?? '').startsWith(p));
}

/**
 * The card the dock draws. Its mode is the PENDING FORM's, not the set's: a set
 * with a required form reports a required card, but the dock only ever shows for
 * an optional pending form (the required ones are behind the wall), for example
 * the sign-up set's survey once its required forms are done. Drawn as required
 * it offered Continue and no way to decline.
 *
 * And its WORDS are the form's when the set's card was written for a required
 * run (`card.setMode`, sent by the API): "Before you carry on" over a survey
 * reads like the wall it is not. The form's title, with its intro as the line or
 * no line at all. The wall at /prompt is untouched; it draws the set's card.
 * Mirror of the app's `promptNudgeCard` in user_form_prompt_card.dart.
 */
export function promptDockCard(pending) {
  if (!pending?.card) return null;
  const mode = pending.mode ?? pending.card.mode;
  const card = {...pending.card, mode};
  if (mode !== 'required' && pending.card.setMode === 'required') {
    card.title = trimmed(pending.title) || card.title;
    card.line = trimmed(pending.intro) || null;
  }
  return card;
}
